/*
 * klaviyo-bis.js
 *
 * Unified Back in Stock submission module. Exposes window.bisSubmit,
 * a single entry point that routes to either the legacy AMP Back in Stock
 * app SDK or Klaviyo's client API depending on the theme setting
 * `bis_backend` (read from <meta name="bis-backend">).
 *
 * Success/error callback shape matches the original BIS.create() contract
 * so existing notifyme.js / quick-add.liquid handlers work unchanged:
 *   - Success: callback(null, { status: 'OK', backend })
 *   - Validation error: callback(null, { errors: { field: [msg] } })
 *   - Hard error: callback({ code, message }, null)
 *
 * Klaviyo docs:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
 *     ?company_id={public_key}&revision=2024-10-15
 *
 * Feature-flag rollback: set `bis_backend` to "amp" in Theme Editor to
 * route all submissions back to the AMP app without a redeploy.
 */

(function () {
  'use strict';

  var KLAVIYO_API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var KLAVIYO_SUBSCRIPTIONS_API = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';
  var MAX_RETRIES = 3;
  var AMP_SDK_TIMEOUT_MS = 5000;

  function readMeta(name) {
    var el = document.querySelector('meta[name="' + name + '"]');
    if (!el) return null;
    var val = (el.content || '').trim();
    return val === '' ? null : val;
  }

  function resolveCompanyId() {
    return readMeta('klaviyo-company-id');
  }

  function resolveBackend() {
    return readMeta('bis-backend') || 'amp';
  }

  function resolveMarketingListId() {
    return readMeta('klaviyo-marketing-list-id');
  }

  // Fire-and-forget: subscribe the profile to the configured marketing list and grant
  // email marketing consent. Called only when the user explicitly ticked the
  // 'I agree to subscribe to the mailing list' checkbox. Failures here are logged but
  // do NOT block the BIS success UI — the user's BIS subscription has already landed.
  async function klaviyoSubscribeToMarketingList(params) {
    var companyId = resolveCompanyId();
    var listId = resolveMarketingListId();
    if (!companyId || !listId) return;
    if (!params.email) return;

    var url = KLAVIYO_SUBSCRIPTIONS_API + '?company_id=' + encodeURIComponent(companyId);
    // Intentionally email-only. Do NOT include phone_number here — when both
    // are passed, Klaviyo grants BOTH email and SMS marketing consent, which
    // overreaches the user's intent (they only ticked 'I agree to subscribe to
    // the mailing list', which is email-only). Klaviyo's /client/subscriptions/
    // endpoint determines which channels to grant consent for based on which
    // contact fields are present in this payload, not from the existing profile.
    // The phone has already been captured by the BIS request that ran before
    // this, so we are not losing phone data.
    var body = JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock form',
          profile: { data: { type: 'profile', attributes: { email: params.email } } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
    try {
      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: body
      });
      if (res.status === 202) {
        console.log('[BIS] marketing list subscription accepted (list=' + listId + ')');
      } else {
        console.warn('[BIS] marketing list subscription returned HTTP ' + res.status);
      }
    } catch (err) {
      console.warn('[BIS] marketing list subscription failed:', err);
    }
  }

  function buildKlaviyoPayload(params) {
    var profileAttrs = {};
    if (params.email) profileAttrs.email = params.email;
    if (params.phone) profileAttrs.phone_number = params.phone;
    // Klaviyo's /client/back-in-stock-subscriptions/ endpoint accepts only
    // email, phone_number, external_id, anonymous_id in profile attributes.
    // Custom properties like accepts_marketing must be set via a separate
    // Profile API call or a Klaviyo Flow — they are NOT accepted here and
    // sending them returns 400 "'properties' is not a valid field for the
    // resource 'profile'". Tracked email/SMS consent will be set on the
    // profile via a separate flow downstream of the BIS metric event.

    var channels = [];
    if (params.email) channels.push('EMAIL');
    if (params.phone && params.smsConsent === true) channels.push('SMS');

    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: { type: 'profile', attributes: profileAttrs }
          },
          channels: channels
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + params.variantId
            }
          }
        }
      }
    };
  }

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  async function klaviyoSubmit(params, callback) {
    var companyId = resolveCompanyId();
    if (!companyId) {
      console.error('[BIS] klaviyo-company-id meta tag missing or empty. Cannot submit.');
      return callback({ code: 'MISSING_COMPANY_ID', message: 'Notifications are temporarily unavailable.' }, null);
    }
    if (!params.variantId) {
      console.error('[BIS] variantId missing');
      return callback({ code: 'MISSING_VARIANT', message: 'Please select a size.' }, null);
    }
    if (!params.email && !params.phone) {
      return callback(null, { errors: { email: ['is required'] } });
    }

    var url = KLAVIYO_API + '?company_id=' + encodeURIComponent(companyId);
    var body = JSON.stringify(buildKlaviyoPayload(params));

    for (var attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
      try {
        var res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': KLAVIYO_REVISION
          },
          body: body
        });

        // Klaviyo BIS returns 202 Accepted on success (async enqueue).
        if (res.status === 201 || res.status === 202) {
          // If the user ticked the 'subscribe to mailing list' checkbox, ALSO grant
          // marketing consent + subscribe to the configured marketing list. This is a
          // separate API call because Klaviyo's BIS endpoint does not accept marketing
          // consent fields. Fire-and-forget — failures here don't block the BIS success.
          if (params.emailConsent === true) {
            klaviyoSubscribeToMarketingList(params);
          }
          return callback(null, { status: 'OK', backend: 'klaviyo' });
        }

        if (res.status === 429 && attempt <= MAX_RETRIES) {
          var backoff = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
          console.warn('[BIS] 429 rate-limited, retrying in ' + backoff + 'ms (attempt ' + attempt + '/' + MAX_RETRIES + ')');
          await sleep(backoff);
          continue;
        }

        if (res.status === 400 || res.status === 422) {
          // Validation error. Normalize to the legacy { errors: {field: [msg]} } shape.
          var detail = 'Invalid submission.';
          try {
            var json = await res.json();
            if (json && json.errors && json.errors.length) {
              var first = json.errors[0];
              detail = first.detail || first.title || detail;
            }
          } catch (e) { /* ignore parse error, use default detail */ }
          console.warn('[BIS] Klaviyo validation error:', detail);
          // Map to a field. Klaviyo's BIS 422 is usually email/phone format or variant-id.
          var field = detail.toLowerCase().indexOf('phone') >= 0 ? 'phone' :
                      detail.toLowerCase().indexOf('variant') >= 0 ? 'variant' : 'email';
          var errs = {};
          errs[field] = [detail];
          return callback(null, { errors: errs });
        }

        // Other 4xx/5xx — hard error.
        console.error('[BIS] Klaviyo returned HTTP ' + res.status);
        return callback({ code: 'HTTP_ERROR', status: res.status, message: 'Unable to submit. Please try again.' }, null);

      } catch (err) {
        console.error('[BIS] Network error (attempt ' + attempt + '):', err);
        if (attempt > MAX_RETRIES) {
          return callback({ code: 'NETWORK', message: 'Unable to submit. Please check your connection and try again.' }, null);
        }
        await sleep(500 * attempt);
      }
    }

    return callback({ code: 'RETRY_EXHAUSTED', message: 'Unable to submit. Please try again.' }, null);
  }

  function ampSubmit(params, callback) {
    // Legacy path. Waits for the AMP Back in Stock app SDK to expose window.BIS,
    // then forwards to BIS.create. Preserves original response shape.
    var waited = 0;
    var interval = 100;

    var check = setInterval(function () {
      if (typeof window.BIS !== 'undefined' && typeof window.BIS.create === 'function') {
        clearInterval(check);
        window.BIS.create(
          params.email,
          params.variantId,
          params.productId,
          {
            handle: params.handle,
            sku: params.sku,
            price: params.price,
            accepts_marketing: params.emailConsent !== false
          },
          function (err, result) {
            if (err) {
              console.error('[BIS] AMP error:', err);
              return callback({ code: 'AMP_ERROR', message: 'Unable to submit. Please try again.' }, null);
            }
            if (result && result.status === 'OK') {
              return callback(null, { status: 'OK', backend: 'amp', raw: result });
            }
            if (result && result.errors) {
              return callback(null, { errors: result.errors });
            }
            console.warn('[BIS] Unexpected AMP response:', result);
            return callback({ code: 'AMP_UNKNOWN', message: 'Unexpected response from the server.' }, null);
          }
        );
      } else if ((waited += interval) >= AMP_SDK_TIMEOUT_MS) {
        clearInterval(check);
        console.warn('[BIS] AMP SDK did not load in ' + AMP_SDK_TIMEOUT_MS + 'ms.');
        callback({ code: 'AMP_UNAVAILABLE', message: 'Notification service unavailable. Please try again later.' }, null);
      }
    }, interval);
  }

  /**
   * Submit a Back in Stock subscription.
   *
   * @param {object} params
   * @param {string} params.email          required (or phone) — subscriber email
   * @param {string} [params.phone]        optional — E.164 phone number (+614xxxxxxxx). Phase 2+.
   * @param {string} params.variantId      required — Shopify variant ID
   * @param {string} params.productId      required — Shopify product ID (AMP path only)
   * @param {string} [params.handle]       optional — product handle (AMP path only)
   * @param {string} [params.sku]          optional — variant SKU (AMP path only)
   * @param {string} [params.price]        optional — variant price (AMP path only)
   * @param {boolean} [params.emailConsent=true]  email marketing consent
   * @param {boolean} [params.smsConsent=false]   SMS consent (required for SMS channel)
   * @param {function} callback  (err, result)
   *   - Success:      callback(null, { status: 'OK', backend })
   *   - Validation:   callback(null, { errors: { field: [msg] } })
   *   - Hard failure: callback({ code, message }, null)
   */
  window.bisSubmit = function (params, callback) {
    var backend = resolveBackend();
    console.log('[BIS] Submitting via ' + backend, {
      variantId: params.variantId,
      hasEmail: !!params.email,
      hasPhone: !!params.phone
    });

    if (backend === 'klaviyo') {
      klaviyoSubmit(params, callback);
    } else {
      ampSubmit(params, callback);
    }
  };

  // Debug surface, handy in the browser console during cutover.
  window.klaviyoBIS = {
    resolveCompanyId: resolveCompanyId,
    resolveBackend: resolveBackend,
    buildPayload: buildKlaviyoPayload
  };
})();
